import { useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { api, type ApiError, type ListReportsResult } from '@whinerp/client';
import { EmptyState, ErrorState, Num, Skeleton, type Column } from '@whinerp/ui';
import { formatRelativeDate } from '@whinerp/contracts/format';
import { useFormat, useT } from '../../app/i18n.js';
import { defineScreen } from '../types.js';
import { RecordList } from '../shared/record-list.tsx';
import { ExecutivePackPanel } from './executive-pack-panel.js';
import { AskPanel } from './ask-panel.js';
import { nextSort } from '../shared/use-saved-view.js';

/**
 * SC-53 Reports list.
 *
 * The report builder was reachable at /reports/new and /reports/:id, and
 * nothing listed what had been saved - so a report somebody built was found
 * again only through a dashboard widget that happened to use it.
 *
 * Visibility is a column. A private report and a workspace one look
 * identical otherwise, and which it is decides whether a colleague can see
 * the figures somebody has been quoting.
 */

type Row = ListReportsResult['reports'][number];

function ReportsListScreen(): JSX.Element {
  const t = useT();
  const format = useFormat();
  const navigate = useNavigate();

  const [search, setSearch] = useState('');
  const [sort, setSort] = useState<{ field: string; direction: 'asc' | 'desc' }>({ field: 'updated', direction: 'desc' });
  const [cursorStack, setCursorStack] = useState<string[]>([]);

  const query = useQuery<ListReportsResult, ApiError>({
    queryKey: ['reports'],
    queryFn: () => api.listReports({}),
  });

  /*
   * Narrowed and ordered here rather than by the server.
   *
   * A workspace has tens of these, not tens of thousands. Paging them would
   * mean a cursor, a total and a page size describing a list that fits on one
   * screen - and the toolbar's search is honest about what it does: it
   * narrows what is already loaded.
   */
  const rows = useMemo(() => {
    const term = search.trim().toLowerCase();
    const all = query.data?.reports ?? [];
    const found = term === '' ? all : all.filter((row) => row.name.toLowerCase().includes(term));
    const direction = sort.direction === 'asc' ? 1 : -1;
    return [...found].sort((a, b) => sort.field === 'updated'
        ? a.updatedAt.localeCompare(b.updatedAt) * direction
        : a.name.localeCompare(b.name) * direction);
  }, [query.data, search, sort]);

  const columns = useMemo<Column<Row>[]>(
    () => [
      {
        key: 'name',
        header: t('list.column.name'),
        sortField: 'name',
        essential: true,
        cell: (row) => row.name,
      },
      {
        key: 'about',
        header: t('list.column.about'),
        essential: true,
        cell: (row) => row.objectKey,
      },
      {
        key: 'visibility',
        header: t('list.column.visibility'),
        hideOnCard: true,
        cell: (row) => row.visibility,
      },
      {
        key: 'updated',
        header: t('list.column.updated'),
        sortField: 'updated',
        hideOnCard: true,
        cell: (row) => <Num className="text-ink-meta">{formatRelativeDate(row.updatedAt, format)}</Num>,
      },
    ],
    [t, format],
  );

  return (
    <div className="flex w-full flex-col gap-2">
    {/* Standing analyses that are not a single report (F-101, F-103). */}
    <nav aria-label={t('analyses.title')} className="flex flex-wrap justify-end gap-3">
      <Link data-analysis-link="cohorts" to="/analytics/cohorts" className="text-dense text-bronze-text hover:text-ink">{t('cohorts.title')}</Link>
      <Link data-analysis-link="coaching" to="/analytics/coaching" className="text-dense text-bronze-text hover:text-ink">{t('coaching.title')}</Link>
    </nav>
    <AskPanel />
    <ExecutivePackPanel />
    <RecordList<Row>
      title={t('list.reports.title')}
      icon="ChartBar"
      rowId={(row) => row.reportId}
      recordHref={(row) => `/reports/${row.reportId}`}
      columns={columns}
      page={{ rows, total: rows.length, hasMore: false, nextCursor: null }}
      loading={query.isPending}
      error={query.isError ? { message: query.error.message, rule: query.error.rule } : null}
      onRetry={() => void query.refetch()}
      refreshing={query.isFetching}
      search={search}
      onSearchChange={setSearch}
      sort={sort}
      onSortChange={(field) => setSort((current) => nextSort(current, field))}
      cursorStack={cursorStack}
      onCursorStackChange={setCursorStack}
      pageSize={rows.length || 1}
      onNew={() => navigate('/reports/new')}
      onClearFilters={() => setSearch('')}
      filtered={search.trim().length > 0}
      emptyTitle={t('list.reports.empty.title')}
      emptyBody={t('list.reports.empty.body')}
      emptyIllustration="ChartBar"
    />
    </div>
  );
}

function ReportsListEmpty(): JSX.Element {
  const t = useT();
  return (
    <EmptyState
      reason="no-records"
      illustration="ChartBar"
      title={t('list.reports.empty.title')}
      body={t('list.reports.empty.body')}
      primary={{ label: t('action.new'), icon: 'Plus', href: '/reports/new' }}
    />
  );
}

function ReportsListLoading(): JSX.Element {
  return (
    <div className="flex flex-col gap-px" role="status" aria-busy="true">
      <span className="sr-only">Loading</span>
      {Array.from({ length: 6 }, (_, index) => (
        <Skeleton key={index} className="h-row-comfortable" />
      ))}
    </div>
  );
}

function ReportsListError({ error, retry }: { error: unknown; retry: () => void }): JSX.Element {
  return (
    <ErrorState
      title="Reports could not be loaded"
      body={error instanceof Error ? error.message : 'The list did not load.'}
      onRetry={retry}
    />
  );
}

export const screen = defineScreen({
  id: 'SC-53',
  route: '/reports',
  surface: 'page',
  title: 'Reports list',
  features: ['F-097'],
  entitlement: 'F-097',
  reachedBy: [{ kind: 'nav', item: 'reportsList' }],
  components: ['DataTable', 'FilterChips'],
  elements: ['RecordList'],
  states: {
    empty: ReportsListEmpty,
    loading: ReportsListLoading,
    error: ReportsListError,
  },
  component: ReportsListScreen,
});

export default screen;
